import React, { useState, useEffect } from 'react';
import '../AssessmentForm.css';

const reportID = '050B407B-7499-4182-8F80-4A341822C69C'; // Placeholder for report ID if needed

const GAD7Tab = ({ reportId, api, onSubmit, onSave , isPublished}) => {
  const [responses, setResponses] = useState({});
  const [reAssess, setReAssess] = useState(null);
  const [savingStates, setSavingStates] = useState({});
  const [saveTimeouts, setSaveTimeouts] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const scaleLabels = [
    { value: 0, label: 'Not at all' },
    { value: 1, label: 'Several days' },
    { value: 2, label: 'More than half the days' },
    { value: 3, label: 'Nearly every day' }
  ];

  const questions = [
    "Feeling nervous, anxious or on edge",
    "Not being able to stop or control worrying",
    "Worrying too much about different things",
    "Trouble relaxing",
    "Being so restless that it is hard to sit still",
    "Becoming easily annoyed or irritable",
    "Feeling afraid as if something awful might happen"
  ];

  const fieldNames = [
    "gad7_answer_1",
    "gad7_answer_2",
    "gad7_answer_3",
    "gad7_answer_4",
    "gad7_answer_5",
    "gad7_answer_6",
    "gad7_answer_7"
  ];

  // Load existing responses from API
  useEffect(() => {
    const loadExistingResponses = async () => {
      try {
        setLoading(true);
        const effectiveReportId = reportId || reportID;
        const response = await api.webAPIFetch(`/api/PatientCaseReports/${effectiveReportId}/sections/GAD7Assessment`);

        if (response.ok) {
          const data = await response.json();

          // Convert field names back to question indexes
          const indexedResponses = {};
          fieldNames.forEach((fieldName, index) => {
            if (data.questions && data.questions[fieldName] !== undefined && data.questions[fieldName] !== null) {
              indexedResponses[index] = data.questions[fieldName];
            }
          });

          setResponses(indexedResponses);
          setReAssess(data.reAssess);

          // Mark all loaded responses as saved
          const savedStates = {};
          Object.keys(indexedResponses).forEach(index => {
            savedStates[index] = 'saved';
          });
          if (data.reAssess !== null && data.reAssess !== undefined) {
            savedStates['re_assess'] = 'saved';
          }
          setSavingStates(savedStates);
        }
      } catch (error) {
        console.error('Error loading existing responses:', error);
        setLoadError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadExistingResponses();
  }, [reportId]);

  // API call for saving individual response
  const saveResponse = async (questionIndex, value) => {
    if (isPublished) return;
    console.log(`Saving GAD-7 response: Question ${questionIndex + 1}, Value: ${value}`);

    const fieldName = fieldNames[questionIndex];
    const effectiveReportId = reportId || reportID;

    try {
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${effectiveReportId}/sections/GAD7Assessment/fields/${fieldName}`, {
        method: 'PATCH',
        body: JSON.stringify({
          value: parseInt(value)
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setSavingStates(prev => ({
        ...prev,
        [questionIndex]: 'saved'
      }));

      if (onSave) {
        onSave();
      }

    } catch (error) {
      console.error(`Error saving response for question ${questionIndex + 1}:`, error);
      setSavingStates(prev => ({
        ...prev,
        [questionIndex]: 'error'
      }));
    }
  };

  // API call for saving re-assess field
  const saveReAssess = async (value) => {
    console.log(`Saving GAD-7 re-assess: ${value}`);

    const effectiveReportId = reportId || reportID;

    try {
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${effectiveReportId}/sections/GAD7Assessment/fields/re_assess`, {
        method: 'PATCH',
        body: JSON.stringify({
          value: value
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setSavingStates(prev => ({
        ...prev,
        're_assess': 'saved'
      }));

      if (onSave) {
        onSave();
      }

    } catch (error) {
      console.error('Error saving re-assess:', error);
      setSavingStates(prev => ({
        ...prev,
        're_assess': 'error'
      }));
    }
  };

  const handleResponseChange = (questionIndex, value) => {
    setResponses(prev => ({
      ...prev,
      [questionIndex]: parseInt(value)
    }));

    // Clear any existing timeout for this question
    if (saveTimeouts[questionIndex]) {
      clearTimeout(saveTimeouts[questionIndex]);
    }

    // Set saving state immediately
    setSavingStates(prev => ({
      ...prev,
      [questionIndex]: 'saving'
    }));

    // Debounce the API call
    const timeoutId = setTimeout(() => {
      saveResponse(questionIndex, value);
    }, 400);

    setSaveTimeouts(prev => ({
      ...prev,
      [questionIndex]: timeoutId
    }));
  };

  const handleReAssessChange = (value) => {
    setReAssess(value);

    // Clear any existing timeout
    if (saveTimeouts['re_assess']) {
      clearTimeout(saveTimeouts['re_assess']);
    }

    // Set saving state
    setSavingStates(prev => ({
      ...prev,
      're_assess': 'saving'
    }));

    // Debounce the API call
    const timeoutId = setTimeout(() => {
      saveReAssess(value);
    }, 400);

    setSaveTimeouts(prev => ({
      ...prev,
      're_assess': timeoutId
    }));
  };

  const calculateScore = () => {
    const completedItems = Object.keys(responses).length;

    if (completedItems !== questions.length) {
      return null; // Must answer all questions for valid score
    }

    const totalScore = questions.reduce((sum, _, index) => {
      return sum + (responses[index] || 0);
    }, 0);

    return totalScore;
  };

  const getSeverityLevel = (score) => {
    if (score === null) return null;
    if (score <= 4) return 'Minimal anxiety';
    if (score <= 9) return 'Mild anxiety';
    if (score <= 14) return 'Moderate anxiety';
    return 'Severe anxiety';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const score = calculateScore();
    if (score === null) {
      alert('Please complete all questions.');
      return;
    }

    const result = {
      assessmentType: 'GAD-7',
      responses,
      reAssess,
      score,
      severity: getSeverityLevel(score),
      completedAt: new Date().toISOString(),
      timeframe: 'Past 2 weeks'
    };

    if (onSubmit) {
      await onSubmit(result);
    }
  };

  const completedCount = Object.keys(responses).length;
  const canSubmit = completedCount === questions.length;
  const currentScore = calculateScore();

  if (loading) {
    return (
      <div className="assessment-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading existing responses...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="assessment-container">
        <div className="error-state">
          <p>Error loading responses: {loadError}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-container">
      <fieldset disabled={isPublished} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="assessment-header">
        <h2>GAD-7 Assessment</h2>
        <p><strong>Generalized Anxiety Disorder 7-item scale</strong></p>
        <p><strong>Instructions:</strong> Over the <strong>last 2 weeks</strong>, how often have you been bothered by any of the following problems?</p>
      </div>

      <div className="scale-header sticky-header">
        <div className="scale-labels gad7-scale-header">
          <div className="scale-label">
            {/* Empty space above question column */}
          </div>
          {scaleLabels.map(scale => (
            <div key={scale.value} className="scale-label gad7-scale" aria-label={`Scale option: ${scale.label}`}>
              <span className="scale-value">{scale.value}</span>
              <span className="scale-text">{scale.label}</span>
            </div>
          ))}
          <div className="scale-label save-status-header">
            <span className="scale-text">Status</span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="assessment-form">
        <div className="questions-container gad7-grid">
          {questions.map((question, index) => (
            <fieldset
              key={index}
              className="question-fieldset"
            >
              <legend className="question-legend">
                {index + 1}. {question}
              </legend>
              <div className="response-options gad7-options">
                {scaleLabels.map(scale => (
                  <div key={scale.value} className="radio-container">
                    <input
                      type="radio"
                      id={`gad7_${index}_${scale.value}`}
                      name={`gad7_question_${index}`}
                      value={scale.value}
                      checked={responses[index] === scale.value}
                      onChange={(e) => handleResponseChange(index, e.target.value)}
                      className="response-radio"
                    />
                    <label
                      htmlFor={`gad7_${index}_${scale.value}`}
                      className="radio-label sr-only"
                    >
                      {scale.label}
                    </label>
                  </div>
                ))}
              </div>
              <div className="save-status-container">
                {savingStates[index] === 'saving' && (
                  <div className="save-status saving" aria-label="Saving response">
                    <div className="spinner"></div>
                  </div>
                )}
                {savingStates[index] === 'saved' && (
                  <div className="save-status saved" aria-label="Response saved">
                    <span className="checkmark">✓</span>
                  </div>
                )}
                {savingStates[index] === 'error' && (
                  <div className="save-status error" aria-label="Save failed">
                    <span className="error-mark">⚠</span>
                  </div>
                )}
              </div>
      
      </fieldset>
          ))}
        </div>

        <div className="re-assess-section">
          <fieldset className="question-fieldset">
            <legend className="question-legend">
              If you checked off any problems, how difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?
            </legend>
            <div className="re-assess-options">
              <div className="radio-container-block">
                <input
                  type="radio"
                  id="gad7_re_assess_not_difficult"
                  name="gad7_re_assess"
                  value="false"
                  checked={reAssess === false}
                  onChange={() => handleReAssessChange(false)}
                  className="response-radio"
                />
                <label htmlFor="gad7_re_assess_not_difficult" className="radio-label-block">
                  Not difficult at all
                </label>
              </div>
              <div className="radio-container-block">
                <input
                  type="radio"
                  id="gad7_re_assess_somewhat"
                  name="gad7_re_assess"
                  value="true"
                  checked={reAssess === true}
                  onChange={() => handleReAssessChange(true)}
                  className="response-radio"
                />
                <label htmlFor="gad7_re_assess_somewhat" className="radio-label-block">
                  Somewhat difficult / Very difficult / Extremely difficult
                </label>
              </div>
            </div>
            <div className="save-status-container">
              {savingStates['re_assess'] === 'saving' && (
                <div className="save-status saving" aria-label="Saving response">
                  <div className="spinner"></div>
                </div>
              )}
              {savingStates['re_assess'] === 'saved' && (
                <div className="save-status saved" aria-label="Response saved">
                  <span className="checkmark">✓</span>
                </div>
              )}
              {savingStates['re_assess'] === 'error' && (
                <div className="save-status error" aria-label="Save failed">
                  <span className="error-mark">⚠</span>
                </div>
              )}
            </div>
    
      </fieldset>
        </div>

        <div className="form-actions">
          <div className="completion-status">
            <div>Completed: {completedCount} / {questions.length}</div>
            {currentScore !== null && (
              <div className="score-display">
                <strong>Total Score: {currentScore}</strong>
                <div className="severity-level">{getSeverityLevel(currentScore)}</div>
              </div>
            )}
          </div>
          <button
            type="submit"
            className="submit-button"
            disabled={!canSubmit}
          >
            Submit Assessment
          </button>
        </div>
      </form>

      <div className="assessment-info">
        <h3>About GAD-7</h3>
        <ul>
          <li>Time frame: Past 2 weeks</li>
          <li>Score range: 0-21</li>
          <li>Severity levels:
            <ul>
              <li>0-4: Minimal anxiety</li>
              <li>5-9: Mild anxiety</li>
              <li>10-14: Moderate anxiety</li>
              <li>15-21: Severe anxiety</li>
            </ul>
          </li>
        </ul>
      </div>
      </fieldset>

    </div>
  );
};

export default GAD7Tab;
